import CartProductsApiService from '@package/core/src/cartProducts/ApiService';
import { MiniCartResponse } from '@package/core/src/cartProducts/types';
import { ShoppingCartStepType } from '@package/core/src/shoppingCart';
import { apiClient } from '@package/hooks/src/services';
import useCartitems from '@package/hooks/src/useCartItems';
import useCheckRedirect from '@package/hooks/src/useCheckRedirect';
import useHover from '@package/hooks/src/useHover';
import useMediaQuery from '@package/hooks/src/useMediaQuery';
import useShoppingCart from '@package/hooks/src/useShoppingCart';
import clsx from 'clsx';
import config from 'config-base';
import dynamic from 'next/dynamic';
import { useEffect, useRef, useState } from 'react';
import EmptyCartPopUp from './__components/EmptyCartPopUp';
import styles from './CartPopup.module.css';
import { CartPopupComponentProps } from './types';
import Image from '../Image';
import MiniCartPopup from './__components/MiniCartPopUp';

const Backdrop = dynamic(() => import('../Backdrop'));

const CartPopup = ({ topPosition = 0, toggleVisible }: CartPopupComponentProps) => {
  const [isVisible, setIsVisible] = useState<boolean>(false);
  const [miniCartData, setMiniCartData] = useState<MiniCartResponse | null>(null);
  const [height, setHeight] = useState<number>(445);
  const { deleteProduct } = useShoppingCart({ disableGetShoppingCart: false, mainCart: true });
  const [showTopShade, setShowTopShade] = useState<boolean>(false);
  const [showBottomShade, setShowBottomShade] = useState<boolean>(false);
  const hoverRef = useRef<HTMLDivElement>(null);
  const { isHover, handleHoverEnter } = useHover(hoverRef);
  const mobile: boolean = useMediaQuery('(max-width:1279px)');
  const tablet: boolean = useMediaQuery('(max-width:767px)');
  const { cartItems } = useCartitems();
  const { redirectStart } = useCheckRedirect();

  const getList = async () => {
    const cartProductsApiService = new CartProductsApiService(apiClient);
    const res = await cartProductsApiService.getList();

    if (!res) return setMiniCartData({ products: [], orderId: 0, subTotal: 0 });
    return setMiniCartData(res?.data);
  };

  useEffect(() => {
    window.addEventListener('mousedown', handleClickOutside);
    return () => {
      window.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleClickOutside = ({ target }: MouseEvent): void => {
    if (!hoverRef.current?.contains(target as Node)) {
      setIsVisible(false);
    }
  };

  useEffect(() => {
    if (!isHover) return;
    const element = document.getElementById('top-banner-edit');
    const headerHeight = element ? topPosition + 102 : topPosition + 82;
    setHeight(window.innerHeight - headerHeight >= 445 ? 445 : window.innerHeight - headerHeight);
    void getList();
  }, [isHover]);

  useEffect(() => {
    hoverCartScroll();
  }, [miniCartData]);

  useEffect(() => {
    if (document) {
      const htmlRoot = document?.querySelector('html');
      if (isVisible) {
        if (mobile && window.scrollY < topPosition) {
          window.scrollTo({ top: 0 });
        }
        htmlRoot?.classList.add('preventScroll');
      } else {
        htmlRoot?.classList.remove('preventScroll');
      }
    }
  }, [isVisible]);

  const hoverCartScroll = () => {
    const itemList: HTMLElement | null = document.getElementById('product-list');
    if (!itemList) return;
    const scrollH = itemList?.scrollHeight;

    if (scrollH <= itemList.clientHeight) {
      setShowTopShade(false);
      setShowBottomShade(false);
      return;
    }

    const scrollT = itemList.scrollTop;
    const scrollB = Math.abs(scrollH - scrollT - itemList.clientHeight);

    if (scrollT > 0) {
      setShowTopShade(true);
    } else {
      setShowTopShade(false);
    }

    if (scrollB > 1) {
      setShowBottomShade(true);
    } else {
      setShowBottomShade(false);
    }
  };

  const handleClick = () => {
    if (mobile && config.brandCode === '0') {
      setIsVisible(!isVisible);
    } else {
      window.location.href = ShoppingCartStepType.ShoppingCart;
    }
    if (mobile) {
      handleHoverEnter();
    }
    toggleVisible?.();
  };

  const handleClose = () => {
    setIsVisible(false);
  };

  return (
    <div ref={hoverRef} className={clsx('relative', { [styles.cartItemsAGD]: config.brandCode === '2' })}>
      {config.brandCode === '0' && (
        <div
          id="cart-icon"
          data-testid={'cart-icon'}
          className={clsx(styles.cartItemsContainer, {
            [styles.cartActive]: isVisible,
          })}
          onClick={handleClick}
        >
          {cartItems && cartItems > 0 ? <span className={styles.badget}>{cartItems}</span> : null}
        </div>
      )}
      {config.brandCode === '2' && (
        <div className={styles.cartIconAGD} onClick={handleClick}>
          <span className={styles.badgetAGD}>{cartItems ? cartItems : 0}</span>
          {tablet ? (
            <Image src="/marketplace-static/images/icons/agd/shoping-cart-empty.svg" width={32} height={32} alt="AGD" />
          ) : (
            <Image
              src="/marketplace-static/images/icons/agd/shopping-cart-empty-desktop.svg"
              width={32}
              height={32}
              alt="AGD"
            />
          )}
        </div>
      )}
      <div
        id="cart-popup"
        className={clsx(styles.cartPopup, {
          [styles.cartPopupBodySlideLeftAnimation]: isVisible && miniCartData,
        })}
        style={mobile ? { top: `${topPosition}px`, height: `calc(100% - ${topPosition}px)` } : {}}
      >
        {miniCartData && isHover && (
          <div className={styles.cartContent}>
            {miniCartData.products.length > 0 ? (
              <MiniCartPopup
                cartItems={cartItems}
                showTopShade={showTopShade}
                showBottomShade={showBottomShade}
                hoverCartScroll={hoverCartScroll}
                mobile={mobile}
                height={height}
                miniCartData={miniCartData}
                deleteProduct={deleteProduct}
                getList={getList}
                redirectStart={redirectStart}
              />
            ) : (
              <EmptyCartPopUp redirectStart={redirectStart} handleClose={handleClose} />
            )}
          </div>
        )}
      </div>

      {mobile && isVisible && miniCartData && (
        <Backdrop style={{ top: `${topPosition}px` }} className={styles.bodyBackdrop} onClick={handleClose} />
      )}
    </div>
  );
};

export default CartPopup;
